import { forwardRef, type ReactNode, type CSSProperties } from 'react';
import { cn } from '../../lib/cn';
import {
  SCROLLBAR_CLASS,
  SCROLLBAR_TRACK_BG_CLASS,
  SCROLLBAR_TRACK_BORDER_CLASS,
} from '../../lib/scrollbar';
import { type SButtonColor, type SButtonSize } from '../SButton';
import { SFooter } from '../SFooter';

export type SPopupType = 'default' | 'light';

export interface SPopupSubmitButton {
  label?: string;
  color?: SButtonColor;
  outline?: boolean;
  size?: SButtonSize;
}

export interface SPopupProps {
  /** 헤더 제목 */
  popupTitle?: string;
  /** 타입 (헤더 색상) */
  type?: SPopupType;
  /** 하단 footer 표시 여부 */
  useFooter?: boolean;
  /** 확인 버튼 설정 */
  submitButton?: SPopupSubmitButton;
  /** 확인 버튼 클릭 (sdSubmit) */
  onSubmit?: () => void;
  /** footer 좌측 영역 */
  footerLeft?: ReactNode;
  /** 본문 기본 패딩을 없앱니다. 표를 가장자리까지 채우는 등 본문이 직접 여백을 다룰 때만 사용합니다. */
  noPadding?: boolean;
  /** 본문 */
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/**
 * SPopup — sd-popup 포팅. 헤더(제목)+본문(children)+푸터(확인 버튼) 패널.
 * 헤더 색상은 type(default/inverse), 색은 `--sys-color-*` 토큰.
 */
export const SPopup = forwardRef<HTMLDivElement, SPopupProps>(function SPopup(
  {
    popupTitle = '',
    type = 'default',
    useFooter = false,
    submitButton,
    onSubmit,
    footerLeft,
    noPadding = false,
    children,
    className,
    style,
  },
  ref,
) {
  const isLight = type === 'light';
  const headerBg = isLight
    ? 'var(--cmp-popup-header-bg-inverse)'
    : 'var(--cmp-popup-header-bg-default)';
  const headerColor = isLight
    ? 'var(--cmp-popup-header-title-inverse)'
    : 'var(--cmp-popup-header-title-default)';

  return (
    <div
      ref={ref}
      className={cn('flex h-full w-full flex-col overflow-hidden', className)}
      style={{ background: 'var(--cmp-popup-bg)', ...style }}
    >
      <header
        className="flex h-[50px] w-full flex-shrink-0 items-center justify-center px-(--cmp-popup-header-paddingX) py-(--cmp-popup-header-paddingY)"
        style={{ background: headerBg }}
      >
        <h2
          className="typo-heading-lg m-0 overflow-hidden text-ellipsis whitespace-nowrap text-center"
          style={{ color: headerColor }}
        >
          {popupTitle}
        </h2>
      </header>

      {/* 본문: 스크롤 영역. 안쪽 여백은 토큰이 정한다 — noPadding 이면 콘텐츠가 직접 담당한다 */}
      <main
        className={cn(
          'box-border min-h-0 flex-1 overflow-auto',
          !noPadding && 'p-(--sys-space-panel-padding-default)',
          SCROLLBAR_CLASS,
          SCROLLBAR_TRACK_BG_CLASS,
          SCROLLBAR_TRACK_BORDER_CLASS,
        )}
      >
        {children}
      </main>

      {useFooter && <SFooter left={footerLeft} button={{ ...submitButton, onClick: onSubmit }} />}
    </div>
  );
});
